import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { junip } from 'lib/junip';
import type { JunipProductReview } from 'types/reviews';
import type {
  JunipProductReviewRaw,
  JunipProductReviewsRaw,
} from 'types/reviews/rawData';
import { convertArrayToCamelCase } from 'utils/convertKeysToCamelCase';
import { partition } from 'utils/partition';

import { useReviewedProducts } from './useReviewedProducts';

export const PRODUCT_REVIEWS_KEY = 'productReviews';

const REVIEWS_COUNT = 50;
const REVIEWS_PER_PAGE = 3;
const PAGE_SIZE = Math.round(REVIEWS_COUNT / REVIEWS_PER_PAGE);

export type UseProductReviewsQueryKey = [
  string,
  string[],
  string | undefined | null,
  boolean | undefined,
];

export interface UseProductReviewsOptions {
  productSlugs?: string[];
  productFilters?: string[];
  ratingFilter?: string | null;
  withMediaFilter?: boolean;
  ageFilter?: string | null;
  reviewsPerPage?: number;
}

export interface UseProductReviewsSelector {
  paginatedReviews: JunipProductReview[][];
}

const cleanProductReviews = (
  res: JunipProductReviewsRaw,
): Array<JunipProductReview> =>
  res.product_reviews
    ? convertArrayToCamelCase<JunipProductReviewRaw, JunipProductReview>(
        res.product_reviews,
      )
    : [];

export const fetchProductReviews = async ({
  queryKey,
}: QueryFunctionContext<UseProductReviewsQueryKey>): Promise<
  JunipProductReview[]
> => {
  const [, productFilters, ratingFilter, withMediaFilter] = queryKey;

  const productFilter = productFilters?.length
    ? productFilters.join(',')
    : null;

  const createEndpoint = () => {
    const params = [
      ...(productFilter ? [`filter[product_ids]=${productFilter}`] : []),
      ...(ratingFilter ? [`filter[rating]=${ratingFilter}`] : []),
      ...(withMediaFilter ? [`filter[media_only]=true`] : []),
      'include=customer,attribute_records',
      `page[size]=${PAGE_SIZE}`,
    ].join('&');

    return `/v1/product_reviews?${params}`;
  };

  const response = await junip<JunipProductReviewsRaw>(createEndpoint());

  return cleanProductReviews(response);
};

export const useProductReviews = ({
  productSlugs,
  productFilters = [],
  ratingFilter = null,
  withMediaFilter = false,
  ageFilter = null,
  reviewsPerPage = 3,
}: UseProductReviewsOptions): UseQueryResult<
  UseProductReviewsSelector,
  unknown
> => {
  // We can filter by product slugs and category to get the reviewed products
  const slugsToFilter = productSlugs?.length ? productSlugs : undefined;

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter,
  });

  // If product filters are defined, use them, otherwise derive numeric IDs from the reviewed products
  const productIdFilters =
    productFilters?.length > 0
      ? productFilters
      : (reviewedProducts?.map((p) => p.id.toString()) ?? []);

  const queryKey: UseProductReviewsQueryKey = [
    PRODUCT_REVIEWS_KEY,
    productIdFilters,
    ratingFilter,
    withMediaFilter,
  ];

  return useQuery<
    JunipProductReview[],
    unknown,
    UseProductReviewsSelector,
    UseProductReviewsQueryKey
  >({
    queryKey,
    queryFn: fetchProductReviews,
    enabled: !slugsToFilter?.length || productIdFilters.length > 0,
    placeholderData: keepPreviousData,
    select: (data) => {
      // We only want reviews with body text
      const reviewsWithBody = data?.filter((review) => review.body !== null);

      // Age is a Junip review `attributeRecord` rather than an API-level filter,
      // so we narrow the fetched reviews client-side here. A review with no
      // matching record is excluded when the filter is active.
      const filteredReviews = ageFilter
        ? reviewsWithBody?.filter((review) =>
            review.attributeRecords?.some(
              (record) => record.name === 'Age' && record.value === ageFilter,
            ),
          )
        : reviewsWithBody;

      // We return all the reviews partitioned into a paginated list
      return {
        paginatedReviews: filteredReviews
          ? partition<JunipProductReview>(filteredReviews, reviewsPerPage)
          : [],
      };
    },
  });
};
